import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import {
  Row,
  Col,
  Button,
  Card,
  Badge,
  Spinner,
  Container,
  Image,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import * as Yup from "yup";
import { base_url } from "../utils/axiosConfig";
import axios from "axios";

// Import India Data
import { stateDistricts, allStatesAndUTs } from "../utils/Data";

const RetailerOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [retailer, setRetailer] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);
  const [showPendingPayment, setShowPendingPayment] = useState(false);
  const [existingOrder, setExistingOrder] = useState(null);
  const [availableDistricts, setAvailableDistricts] = useState([]);

  const initialValues = {
    fullName: "",
    dob: "",
    gender: "",
    bloodGroup: "",
    email: "",
    mobile: "",
    address_line: "",
    city: "",
    district: "",
    state: "",
    country: "India",
    pincode: "",
    profilePhoto: null,
    confirmation: false,
  };

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .required("Full name is required")
      .min(2, "Full name must be at least 2 characters"),
    dob: Yup.date().max(new Date(), "Date of birth cannot be in the future"),
    gender: Yup.string().oneOf(["Male", "Female", "Other"]),
    bloodGroup: Yup.string().oneOf([
      "A+",
      "A-",
      "B+",
      "B-",
      "AB+",
      "AB-",
      "O+",
      "O-",
    ]),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
    address_line: Yup.string().required("Address line is required"),
    city: Yup.string().required("City is required"),
    district: Yup.string().required("District is required"),
    state: Yup.string().required("State is required"),
    country: Yup.string().required("Country is required"),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
    profilePhoto: Yup.mixed()
      .required("Profile Photo is required")
      .test("fileSize", "File size must be less than 2MB", (value) => {
        if (!value) return true;
        return value.size <= 2 * 1024 * 1024;
      })
      .test("fileType", "Only JPG, JPEG, or PNG files are allowed", (value) => {
        if (!value) return true;
        return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
      }),
    confirmation: Yup.boolean().oneOf(
      [true],
      "You must confirm the customer onboarding",
    ),
  });

  const fetchRetailerById = async () => {
    try {
      const response = await axios.get(
        `${base_url}product/fetchRetailer/${id}`,
      );
      const data = response.data.data;

      setRetailer(data.retailerId);
      setStockData(data);

      // Expired QR Code
      if (
        data.status === "expired" ||
        data.status === "used" ||
        data.status === "already_registered"
      ) {
        setShowExpiredMessage(true);
        setTimeout(() => navigate("/"), 10000);
        return;
      }

      // Pending Payment
      if (data.retailerOrderId && data.retailerOrderId?.transaction?.status !== "paid") {
        setShowPendingPayment(true);
        setExistingOrder(data.retailerOrderId._id);
      }
    } catch (error) {
      console.error("Error fetching retailer:", error);
      toast.error("Failed to load retailer information");
      navigate("/");
    }
  };

  useEffect(() => {
    if (id) fetchRetailerById();
  }, [id]);

  // Handle State Change
  const handleStateChange = (e, setFieldValue) => {
    const selectedState = e.target.value;
    setFieldValue("state", selectedState);
    setFieldValue("district", ""); // Reset district

    if (selectedState && stateDistricts[selectedState]) {
      setAvailableDistricts(stateDistricts[selectedState]);
    } else {
      setAvailableDistricts([]);
    }
  };

  const getProductDetails = () => {
    if (!stockData?.mainProductId?.verticalProducts) return null;
    return stockData.mainProductId.verticalProducts.find(
      (vp) => vp._id === stockData.verticalProductId,
    );
  };

  const product = getProductDetails();
  const amount = product?.price?.amount || 0;
  const gst = amount * 0.18;
  const totalAmount = amount + gst;

  const handleSubmit = async (values) => {
    if (!stockData?.verticalProductId) {
      toast.error("Product information not available");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("fullName", values.fullName);
      formData.append("dob", values.dob || "");
      formData.append("gender", values.gender || "");
      formData.append("bloodGroup", values.bloodGroup || "");
      formData.append("email", values.email);
      formData.append("mobile", values.mobile);
      formData.append("address[address_line]", values.address_line);
      formData.append("address[city]", values.city);
      formData.append("address[district]", values.district);
      formData.append("address[state]", values.state);
      formData.append("address[country]", values.country);
      formData.append("address[pincode]", values.pincode);

      if (values.profilePhoto) {
        formData.append("profilePhoto", values.profilePhoto);
      }

      formData.append(
        "mainProductId",
        stockData.mainProductId?._id || stockData.mainProductId || "",
      );
      formData.append("verticalProductId", stockData.verticalProductId || "");
      formData.append("amount", amount);
      formData.append("amountWithTax", totalAmount);
      formData.append("retailerId", retailer._id);
      formData.append("planType", stockData?.retailerType || "paid");
      formData.append("token",id)

      const response = await axios.post(
        `${base_url}v1/user/productSellByRetailerQrCode`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.status === 201) {
        toast.success("Order created successfully");

        if (retailer?.retailerType === "free") {
          navigate("/");
        } else {
          const { paymentSessionId } = response.data;
          if (paymentSessionId) {
            const cashfree = new window.Cashfree();
            cashfree
              .checkout({
                paymentSessionId,
                redirectTarget: "_self",
                mode: "production",
              })
              .catch((err) => toast.error("Failed to open payment gateway"));
          }
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create order",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayAgain = async () => {
    if (!existingOrder?._id) {
      toast.error("Order not found");
      return;
    }
    try {
      const response = await axios.post(
        `${base_url}v1/user/retryPaymentOfQrCode`,
        {
          orderId: existingOrder._id,
        },
      );

      if (response.data.paymentSessionId) {
        const cashfree = new window.Cashfree();
        cashfree.checkout({
          paymentSessionId: response.data.paymentSessionId,
          redirectTarget: "_self",
          mode: "production",
        });
      }
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
    }
  };

  // Expired QR Screen
  if (showExpiredMessage) {
    return (
      <Container
        className="py-5 d-flex align-items-center justify-content-center"
        style={{ minHeight: "80vh" }}
      >
        <Card
          className="text-center shadow-lg border-0"
          style={{ maxWidth: "500px" }}
        >
          <Card.Body className="p-5">
            <h1 className="display-1 text-danger mb-4">⚠️</h1>
            <h3 className="text-danger">QR Code Expired / Already Used</h3>
            <p className="lead text-muted mt-3">
              Given QR code is expired and User already use this qr and
              registered.
            </p>
            <p className="text-muted">
              Redirecting to home page in 10 seconds...
            </p>
            <Spinner animation="border" variant="danger" className="mt-4" />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Pending Payment Screen
  if (showPendingPayment && existingOrder) {
    return (
      <Container
        className="py-5 d-flex align-items-center justify-content-center"
        style={{ minHeight: "85vh" }}
      >
        <Card
          className="shadow-lg border-0"
          style={{ maxWidth: "600px", width: "100%" }}
        >
          <Card.Body className="p-5">
            <div className="text-center mb-4">
              <h1 className="display-4 text-warning">⏳</h1>
              <h3 className="text-warning">Payment Pending</h3>
            </div>
            <p className="text-center lead mb-4">
              Your payment is pending. Kindly pay here to activate your kit.
            </p>

            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">Order Details</h5>
                <Row className="gy-2">
                  <Col xs={5}>
                    <strong>Full Name:</strong>
                  </Col>
                  <Col xs={7}>{existingOrder.fullName}</Col>
                  <Col xs={5}>
                    <strong>Email:</strong>
                  </Col>
                  <Col xs={7}>{existingOrder.email}</Col>
                  <Col xs={5}>
                    <strong>Mobile:</strong>
                  </Col>
                  <Col xs={7}>{existingOrder.mobile}</Col>
                </Row>
              </Card.Body>
            </Card>

            <Button
              variant="success"
              size="lg"
              onClick={handlePayAgain}
              className="w-100 py-3 fw-bold"
              style={{
                background: "linear-gradient(135deg, #02BE0F, #02880B)",
                border: "none",
              }}
            >
              💳 Pay Again Now
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="text-center mb-5">
        <h1 className="fw-bold display-5 text-primary mb-2">
          Create New Customer Order
        </h1>
        <p className="text-muted fs-5">
          Retailer ID: <strong>{id}</strong>
        </p>
      </div>

      {retailer && (
        <Card className="mb-5 shadow-sm border-0">
          <Card.Body className="bg-light rounded-4">
            <Row className="align-items-center">
              <Col md={8}>
                <h4 className="mb-1">{retailer.name || retailer.fullName}</h4>
                <p className="text-muted mb-2">
                  {retailer.city && `${retailer.city}, ${retailer.state}`}
                </p>
                <div className="d-flex gap-4">
                  <div>
                    <strong>Phone:</strong> {retailer.mobile || retailer.phone}
                  </div>
                  {retailer.email && (
                    <div>
                      <strong>Email:</strong> {retailer.email}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <Badge bg="success" className="fs-6 px-3 py-2">
                  {retailer.retailerType?.toUpperCase() || "RETAILER"}
                </Badge>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {product && (
        <Card className="mb-5 shadow-sm border-0 overflow-hidden">
          <Card.Body className="p-0">
            <Row className="g-0">
              <Col
                md={4}
                className="bg-light d-flex align-items-center justify-content-center p-4"
              >
                <Image
                  src={
                    product.images?.[0] ||
                    "https://via.placeholder.com/300x200?text=No+Image"
                  }
                  alt={product.productName}
                  style={{
                    height: "220px",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  fluid
                />
              </Col>
              <Col md={8}>
                <Card.Body>
                  <Card.Title className="fs-3 fw-bold text-primary">
                    {product.productName || product.name}
                  </Card.Title>
                  <Card.Text className="text-muted">
                    {product.briefDescription ||
                      product.description ||
                      "No description available"}
                  </Card.Text>

                  <div className="mt-4 p-3 border rounded bg-light">
                    <h6 className="mb-3 text-muted">Price Breakdown</h6>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between">
                        <span>Product Price:</span>
                        <span>₹{amount.toFixed(2)}</span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>GST (18%):</span>
                        <span>₹{gst.toFixed(2)}</span>
                      </div>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between fs-5 fw-bold text-success">
                        <span>Total Amount:</span>
                        <span>₹{totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="shadow-lg border-0">
        <Card.Body className="p-4 p-md-5">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form>
                <Row className="g-4">
                  <Col lg={12}>
                    <h5 className="mb-4 pb-2 border-bottom fw-semibold text-primary">
                      👤 Personal Information
                    </h5>
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="fullName"
                      className="form-control form-control-lg"
                      placeholder="Enter full name"
                    />
                    <ErrorMessage
                      name="fullName"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Email <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="email"
                      name="email"
                      className="form-control form-control-lg"
                      placeholder="customer@example.com"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Mobile <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="mobile"
                      className="form-control form-control-lg"
                      placeholder="9876543210"
                    />
                    <ErrorMessage
                      name="mobile"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col lg={12} className="mt-4">
                    <h5 className="mb-4 pb-2 border-bottom fw-semibold text-primary">
                      📍 Address Details
                    </h5>
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      State <span className="text-danger">*</span>
                    </label>
                    <Field
                      as="select"
                      name="state"
                      className="form-select form-select-lg"
                      onChange={(e) => handleStateChange(e, setFieldValue)}
                    >
                      <option value="">Select State / UT</option>
                      {allStatesAndUTs.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="state"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      District <span className="text-danger">*</span>
                    </label>
                    <Field
                      as="select"
                      name="district"
                      className="form-select form-select-lg"
                    >
                      <option value="">Select District</option>
                      {availableDistricts.map((district) => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="district"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      City <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="city"
                      className="form-control form-control-lg"
                      placeholder="Enter city"
                    />
                    <ErrorMessage
                      name="city"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={6}>
                    <label className="form-label fw-semibold">
                      Pincode <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="pincode"
                      className="form-control form-control-lg"
                      placeholder="Enter 6 digit pincode"
                    />
                    <ErrorMessage
                      name="pincode"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col md={12}>
                    <label className="form-label fw-semibold">
                      Address Line <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="address_line"
                      className="form-control form-control-lg"
                      placeholder="House no, Street, Area"
                    />
                    <ErrorMessage
                      name="address_line"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Col>

                  <Col lg={12} className="mt-4">
                    <h5 className="mb-3 fw-semibold text-primary">
                      📸 Profile Photo
                    </h5>
                    <div className="border-2 border-dashed rounded-3 p-4 text-center">
                      <input
                        type="file"
                        className="form-control form-control-lg"
                        accept="image/jpeg,image/png,image/jpg"
                        onChange={(e) =>
                          setFieldValue(
                            "profilePhoto",
                            e.currentTarget.files[0],
                          )
                        }
                      />
                      <ErrorMessage
                        name="profilePhoto"
                        component="div"
                        className="text-danger mt-2"
                      />
                    </div>
                  </Col>

                  <Col lg={12} className="mt-4">
                    <div className="p-4 border rounded-4 bg-light">
                      <div className="form-check">
                        <Field
                          type="checkbox"
                          name="confirmation"
                          className="form-check-input"
                          id="confirmation"
                        />
                        <label
                          htmlFor="confirmation"
                          className="form-check-label fw-semibold"
                        >
                          I confirm that the customer details are correct, they
                          have been properly onboarded
                        </label>
                      </div>
                      <ErrorMessage
                        name="confirmation"
                        component="div"
                        className="text-danger mt-2"
                      />
                    </div>
                  </Col>
                </Row>

                <div className="mt-5 pt-4 border-top">
                  <Button
                    variant="success"
                    size="lg"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-100 py-3 fs-5 fw-bold shadow"
                    style={{
                      background: "linear-gradient(135deg, #02BE0F, #02880B)",
                      border: "none",
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-3"
                        />
                        Processing Order...
                      </>
                    ) : (
                      "✅ Create Order & Proceed"
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RetailerOrder;
